
import { useState, useEffect } from 'react';
import { UserProfile, UserCoin } from '@/types/profile';
import { supabaseExtended } from '@/integrations/supabase/extendedClient';
import { useToast } from '@/components/ui/use-toast';
import { captureException } from '@/utils/sentry';
import { usePublicClient } from 'wagmi';
import { Address, parseAbiItem } from 'viem';

const TOKEN_BALANCE_ABI = [parseAbiItem('function balanceOf(address account) view returns (uint256)')] as const;

interface UseProfileDataProps {
  address: string | undefined;
  shortAddress: string;
  isOwnProfile: boolean;
  currentUserAddress: string | undefined;
}

interface ProfileDataResult {
  profile: UserProfile | null;
  coins: UserCoin[];
  heldCoins: UserCoin[];
  isLoading: boolean;
  error: string | null;
}

export const useProfileData = ({
  address,
  shortAddress,
  isOwnProfile,
  currentUserAddress
}: UseProfileDataProps): ProfileDataResult => {
  const { toast } = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [coins, setCoins] = useState<UserCoin[]>([]);
  const [heldCoins, setHeldCoins] = useState<UserCoin[]>([]);
  const publicClient = usePublicClient({ chainId: 137 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!address) return;
      
      setIsLoading(true);
      setError(null);
      try {
        // Fetch profile data
        const { data: profileData, error: profileError } = await supabaseExtended
          .from('profiles')
          .select('*')
          .eq('wallet_address', address.toLowerCase())
          .single();
        
        if (profileError) {
          console.error("Error fetching profile:", profileError);
          
          // Capture the error in Sentry with detailed context
          captureException(profileError, {
            component: "useProfileData",
            operation: "fetchProfile",
            address: address,
            errorCode: profileError.code,
            errorDetails: profileError.details,
            errorMessage: profileError.message,
            currentUserAddress: currentUserAddress,
            isOwnProfile
          });
          
          // Only show toast for errors other than "not found"
          if (profileError.code !== 'PGRST116') {
            setError("Failed to load profile data");
            toast({
              title: "Error",
              description: "Failed to load profile data",
              variant: "destructive",
            });
          }
        }
        
        if (profileData) {
          setProfile(profileData);
        } else {
          // Create profile if it doesn't exist and it's the current user
          if (isOwnProfile) {
            const { data: { user } } = await supabaseExtended.auth.getUser();
            if (!user) {
              // Wallet-connected visitors may not have a Supabase Auth identity.
              // Avoid inserting a synthetic profile ID because the database
              // trigger creates user_tokens with profiles.id as a foreign key.
              setProfile({ wallet_address: address.toLowerCase(), username: shortAddress });
            } else {
              const newProfile = {
                id: user.id,
                app_name: "Matic Moon",
                wallet_address: address.toLowerCase(),
                username: shortAddress,
              };
              const { data: createdProfile, error: createError } = await supabaseExtended
                .from('profiles')
                .upsert(newProfile, { onConflict: 'id' })
                .select()
                .single();
              
              if (createError) {
                console.error("Error creating profile:", createError);
                captureException(createError, {
                  component: "useProfileData",
                  operation: "createProfile",
                  address: address,
                  errorCode: createError.code,
                  errorDetails: createError.details,
                  currentUserAddress: currentUserAddress
                });
                setError(`Failed to create profile: ${createError.message}`);
              } else if (createdProfile) {
                setProfile(createdProfile);
              }
            }
          } else {
            // If not the owner and profile doesn't exist, just use a placeholder
            setProfile({
              wallet_address: address.toLowerCase(),
              username: shortAddress,
            });
          }
        }
        
        // Fetch coins created by this wallet from production and locally-created records.
        try {
          const safeQuery = async (query: PromiseLike<any>) => {
            try { return await query; } catch { return { data: [] }; }
          };
          const [metricsResult, createdResult, fairResult] = await Promise.all([
            safeQuery((supabaseExtended as any).from('coin_metrics').select('*').eq('creator_address', address.toLowerCase())),
            safeQuery((supabaseExtended as any).from('created_coins').select('*').eq('creator_address', address.toLowerCase())),
            safeQuery((supabaseExtended as any).from('fair_launch_coins').select('*').eq('creator_address', address.toLowerCase())),
          ]);
          const allCoins = [...(metricsResult.data || []), ...(createdResult.data || []), ...(fairResult.data || [])]
            .filter((coin: any, index: number, all: any[]) => index === all.findIndex((candidate) => (candidate.coin_address || candidate.contract_address) === (coin.coin_address || coin.contract_address)));
          const mappedCoins = allCoins.map((coin: any): UserCoin => ({
            id: coin.id || coin.coin_address || coin.contract_address,
            address: coin.creator_address,
            name: coin.name || "Community Token",
            symbol: coin.symbol || coin.name?.substring(0, 4).toUpperCase() || "COIN",
            contract_address: coin.coin_address || coin.contract_address,
            description: coin.description,
            created_at: coin.creation_date || coin.created_at,
          }));
          setCoins(mappedCoins);
          // Holdings are discovered from every token listed in production, not
          // only tokens created by this wallet.
          const [allMetricsResult, allCreatedResult, allFairResult] = await Promise.all([
            safeQuery((supabaseExtended as any).from('coin_metrics').select('*')),
            safeQuery((supabaseExtended as any).from('created_coins').select('*')),
            safeQuery((supabaseExtended as any).from('fair_launch_coins').select('*')),
          ]);
          const heldCandidates = [...(allMetricsResult.data || []), ...(allCreatedResult.data || []), ...(allFairResult.data || [])]
            .filter((coin: any, index: number, all: any[]) => index === all.findIndex((candidate) => (candidate.coin_address || candidate.contract_address) === (coin.coin_address || coin.contract_address)))
            .map((coin: any): UserCoin => ({
              id: coin.id || coin.coin_address || coin.contract_address,
              address: coin.creator_address || '',
              name: coin.name || "Community Token",
              symbol: coin.symbol || coin.name?.substring(0, 4).toUpperCase() || "COIN",
              contract_address: coin.coin_address || coin.contract_address,
              description: coin.description,
              created_at: coin.creation_date || coin.created_at,
            }));
          if (publicClient && heldCandidates.length) {
            const balances = await Promise.all(heldCandidates.map(async (coin) => {
              if (!coin.contract_address || !/^0x[a-fA-F0-9]{40}$/.test(coin.contract_address)) return null;
              try {
                const balance = await publicClient.readContract({ address: coin.contract_address as Address, abi: TOKEN_BALANCE_ABI, functionName: 'balanceOf', args: [address as Address] });
                return (balance as bigint) > 0n ? { ...coin, balance: (balance as bigint).toString() } : null;
              } catch { return null; }
            }));
            setHeldCoins(balances.filter(Boolean) as UserCoin[]);
          } else setHeldCoins([]);
        } catch (coinError) {
          console.error("Error processing coins data:", coinError);
          captureException(coinError, {
            component: "useProfileData",
            operation: "processCoins",
            address: address
          });
        }
      } catch (error) {
        console.error("Error in profile data fetching:", error);
        captureException(error, {
          component: "useProfileData",
          operation: "fetchProfile",
          address: address,
          currentUserAddress: currentUserAddress,
          isOwnProfile
        });
        setError("Failed to load profile data");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProfile();
  }, [address, shortAddress, toast, isOwnProfile, currentUserAddress, publicClient]);

  return {
    profile,
    coins,
    heldCoins,
    isLoading,
    error
  };
};
